import { Breadcrumb, IBreadcrumbItem } from '@sanlam-glacier/app-common/components';
import { generatePath, UIMatch, useMatch, useMatches, useNavigate } from 'react-router-dom';
import { useAuth } from 'auth';
import style from './page-breadcrumbs.module.scss';
import { useProposalIdFromParams } from 'hooks/useProposalIdFromParams';
import { clsx } from 'clsx';
import { useInstructionIdFromParams } from 'hooks/useInstructionIdFromParams';
import { useClientIdFromParams } from 'modules/clients/hooks/useClientIdFromParams';
import { useQuery } from '@tanstack/react-query';
import { getClientData } from 'bff/hooks/reporting/ClientInvestmentData';

type Handle = {
  breadcrumb?: {
    text: string;
    navigationPath: string;
    onClick: ((e: React.SyntheticEvent) => void) | undefined;
  };
};

export const PageBreadcrumbs = () => {
  const auth = useAuth();
  const matches = useMatches() as UIMatch<unknown, Handle>[];
  const navigate = useNavigate();
  const proposalId = useProposalIdFromParams();
  const instructionId = useInstructionIdFromParams();
  const clientId = useClientIdFromParams();
  const isDashboard = useMatch('/dashboard');
  const placeHolderClientName = 'Client';
  const clientNavigationPath = '/clients/:clientId';
  const isClientDashboard = useMatch(clientNavigationPath);

  const { data: client } = useQuery(getClientData(clientId ?? undefined));

  let crumbs = matches
    .filter((match) => Boolean(match.handle?.breadcrumb))
    .map((crumb) => crumb.handle.breadcrumb!);

  crumbs = crumbs.map((crumb) => ({
    ...crumb,
    onClick: crumb.navigationPath ? () => navigate(crumb.navigationPath) : undefined
  }));

  if (auth.identificationState === 'unidentified') {
    return null;
  }

  const clientPathPresent = crumbs.some(
    (crumb) => crumb.text === placeHolderClientName && crumb.navigationPath === clientNavigationPath
  );

  if (clientPathPresent && clientId) {
    crumbs = crumbs.map((crumb) =>
      crumb.text === placeHolderClientName && crumb.navigationPath === clientNavigationPath
        ? {
            ...crumb,
            text: client ? `${client.clientFirstName} ${client.clientLastName}` : '',
            navigationPath: generatePath(clientNavigationPath, { clientId: String(clientId) })
          }
        : crumb
    );
  }

  crumbs = crumbs.map((crumb) => ({
    ...crumb,
    onClick: crumb.navigationPath ? () => navigate(crumb.navigationPath) : undefined
  }));

  const breadcrumbItems: IBreadcrumbItem[] = crumbs.map((crumb) => ({
    label: crumb.text,
    onClick: crumb.onClick,
    limitWidth: false
  }));

  if (proposalId) {
    breadcrumbItems.push({
      label: `Proposal #${proposalId}`
    });
  }

  if (instructionId) {
    breadcrumbItems.push({
      label: `Instruction ${instructionId}`
    });
  }

  if (breadcrumbItems.length > 0) {
    breadcrumbItems.at(-1)!.className = clsx(style.currentPageCrumb);
  }

  return (
    <div
      className={
        isDashboard || isClientDashboard
          ? style.dashboardBreadcrumbContainer
          : style.pageBreadcrumbContainer
      }
    >
      <Breadcrumb
        crumbSize={2}
        crumbStart={2}
        className={style.pageBreadcrumbBody}
        crumbs={breadcrumbItems}
      />
    </div>
  );
};
